import { useEffect, useState, type ReactNode } from 'react'
import { formatDate } from '#app/utils/format-date.ts'

export type LegalSection = {
	id: string
	title: string
	body: ReactNode
}

export type LegalLayoutProps = {
	eyebrow?: string
	title: ReactNode
	effectiveDate?: string
	lastUpdatedDate?: string
	intro: ReactNode
	sections: LegalSection[]
}

export function LegalLayout({
	eyebrow = '/ legal',
	title,
	effectiveDate,
	lastUpdatedDate,
	intro,
	sections,
}: LegalLayoutProps) {
	const activeId = useActiveSection(sections.map((s) => s.id))

	return (
		// A `<div>`, not a `<main>`: the marketing layout owns the single main
		// landmark, and every legal page was rendering a second one inside it.
		<div className="bg-[var(--marketing-base)]">
			<section className="relative isolate overflow-hidden border-b border-[var(--marketing-border)] bg-[var(--marketing-base)] pt-16 pb-14 sm:pt-24">
				<div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
					<p className="eyebrow text-[var(--marketing-faint)]">{eyebrow}</p>
					<h1
						id="doc-top"
						tabIndex={-1}
						className="mt-4 max-w-3xl font-[family-name:var(--font-display)] text-[44px] leading-[1.04] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] focus:outline-none sm:text-[56px]"
						style={{ textWrap: 'balance' }}
					>
						{title}
					</h1>

					{(effectiveDate || lastUpdatedDate) && (
						<dl className="mt-8 flex flex-wrap gap-x-10 gap-y-4 font-[family-name:var(--font-mono)] text-[12px]">
							{effectiveDate ? (
								<div>
									<dt className="text-[var(--marketing-faint)]">Effective</dt>
									<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
										{formatLegalDate(effectiveDate)}
									</dd>
								</div>
							) : null}
							{lastUpdatedDate ? (
								<div>
									<dt className="text-[var(--marketing-faint)]">
										Last updated
									</dt>
									<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
										{formatLegalDate(lastUpdatedDate)}
									</dd>
								</div>
							) : null}
						</dl>
					)}

					<div className="prose-legal mt-8 max-w-[48ch]">{intro}</div>
				</div>
			</section>

			<section className="relative py-16">
				<div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-16 lg:px-8">
					{/*
					  Below `lg` the sticky sidebar is gone, which left a 16-section
					  document with no jump links, no progress and no way back - on the
					  viewport where someone is most likely checking a single clause.
					  `<details>` carries the disclosure state, the expanded/collapsed
					  announcement and keyboard operation natively, matching the header's
					  mobile nav. Same `sections` array, so the two can't drift.
					*/}
					<details className="group rounded-lg border border-[var(--marketing-border)] bg-[var(--marketing-elevated)] lg:hidden">
						<summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 px-4 font-[family-name:var(--font-mono)] text-[11px] tracking-[0.2em] text-[var(--marketing-faint)] uppercase [&::-webkit-details-marker]:hidden">
							/ contents
							<span
								aria-hidden
								className="text-[var(--marketing-accent)] transition-transform group-open:rotate-180"
							>
								↓
							</span>
						</summary>
						<nav
							aria-label="Contents"
							onClick={(event) =>
								event.currentTarget.closest('details')?.removeAttribute('open')
							}
							className="border-t border-[var(--marketing-border)] px-2 pb-2"
						>
							<ol>
								{sections.map((s, i) => (
									<li key={s.id}>
										<a
											href={`#${s.id}`}
											className="flex min-h-11 items-center gap-3 rounded-md px-2 text-[14px] text-[var(--marketing-gray)]"
										>
											<span className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--marketing-faint)]">
												{String(i + 1).padStart(2, '0')}
											</span>
											<span style={{ textWrap: 'pretty' }}>{s.title}</span>
										</a>
									</li>
								))}
							</ol>
						</nav>
					</details>

					<aside className="hidden lg:block">
						<nav
							aria-label="Contents"
							className="sticky top-20 max-h-[calc(100dvh-6rem)] overflow-y-auto pr-2"
						>
							<p className="mb-4 font-[family-name:var(--font-mono)] text-[11px] tracking-[0.2em] text-[var(--marketing-faint)] uppercase">
								/ contents
							</p>
							<ol className="space-y-1">
								{sections.map((s, i) => {
									const isActive = activeId === s.id
									return (
										<li key={s.id}>
											<a
												href={`#${s.id}`}
												className={`group flex gap-3 rounded-md py-1.5 pr-3 pl-2 text-[13px] transition-colors ${
													isActive
														? 'text-[var(--marketing-accent)]'
														: 'text-[var(--marketing-gray)] hover:text-[var(--marketing-black)]'
												}`}
											>
												<span
													className={`font-[family-name:var(--font-mono)] text-[11px] ${
														isActive
															? 'text-[var(--marketing-accent)]'
															: 'text-[var(--marketing-faint)]'
													}`}
												>
													{String(i + 1).padStart(2, '0')}
												</span>
												<span style={{ textWrap: 'pretty' }}>{s.title}</span>
											</a>
										</li>
									)
								})}
							</ol>
						</nav>
					</aside>

					<article className="space-y-14">
						{sections.map((s, i) => (
							<LegalSectionBlock
								key={s.id}
								id={s.id}
								number={String(i + 1).padStart(2, '0')}
								title={s.title}
							>
								{s.body}
							</LegalSectionBlock>
						))}

						{/* Only below `lg`: above it the sticky sidebar is always in
						    reach, and this would be a control for a problem the reader
						    does not have. Targets the `<h1>`, not `#top`, so keyboard
						    and screen-reader focus travels with the scroll. */}
						<a
							href="#doc-top"
							className="inline-flex min-h-11 items-center gap-2 border-t border-[var(--marketing-border)] pt-6 font-[family-name:var(--font-mono)] text-[12px] tracking-[0.14em] text-[var(--marketing-faint)] uppercase transition-colors hover:text-[var(--marketing-accent)] lg:hidden"
						>
							<span aria-hidden>↑</span>
							Back to top
						</a>
					</article>
				</div>
			</section>
		</div>
	)
}

function LegalSectionBlock({
	id,
	number,
	title,
	children,
}: {
	id: string
	number: string
	title: string
	children: ReactNode
}) {
	return (
		// The measure lives on the section, not on the prose, so the heading's
		// underline rule and the paragraphs below it end at the same place. It
		// used to be `max-w-[68ch]` on the prose alone: 768px at 15px, which
		// measured 110 real characters per line while the rule ran ~180px wider.
		// `ch` is the width of "0", and DM Sans's average glyph is much narrower
		// than that - 48ch renders about 75 characters, at the top of the 45–75
		// range. Measured, not assumed; re-measure if the face or size changes.
		<section id={id} className="max-w-[48ch] scroll-mt-24">
			<header className="mb-5 flex items-baseline gap-4 border-b border-[var(--marketing-border)] pb-3">
				<span className="font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-accent)]">
					{number}
				</span>
				<h2 className="font-[family-name:var(--font-display)] text-[22px] font-semibold tracking-[-0.01em] text-[var(--marketing-black)] sm:text-[24px]">
					{title}
				</h2>
			</header>
			<div className="prose-legal">{children}</div>
		</section>
	)
}

function useActiveSection(ids: string[]): string | null {
	// Stable string key - caller may pass a fresh array every render.
	const key = ids.join('|')
	const [active, setActive] = useState<string | null>(ids[0] ?? null)
	useEffect(() => {
		if (typeof window === 'undefined' || key.length === 0) return
		const elements = key
			.split('|')
			.map((id) => document.getElementById(id))
			.filter((el): el is HTMLElement => el !== null)
		if (elements.length === 0) return
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
				if (visible[0]) setActive(visible[0].target.id)
			},
			{ rootMargin: '-20% 0px -65% 0px', threshold: 0 },
		)
		for (const el of elements) observer.observe(el)
		return () => observer.disconnect()
	}, [key])
	return active
}

function formatLegalDate(value: string): string {
	const trimmed = value.trim()
	let d: Date | null = null
	if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
		d = new Date(trimmed)
	} else if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
		const [dd, mm, yyyy] = trimmed.split('/')
		d = new Date(`${yyyy}-${mm}-${dd}`)
	}
	if (!d || Number.isNaN(d.getTime())) return value
	return formatDate(d, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
	})
}
